/**
 * Skill suggestion rendering for conversational onboarding.
 *
 * Displays inline connect buttons when the agent detects
 * the user needs a skill that is not yet connected.
 */

import { html, nothing } from "lit";
import type { SkillSuggestion } from "../types/chat-types";

export type SkillSuggestionConnectHandler = (suggestion: SkillSuggestion) => void;

/**
 * Renders an inline skill suggestion card with connect button.
 */
export function renderSkillSuggestion(
  suggestion: SkillSuggestion,
  onConnect?: SkillSuggestionConnectHandler,
) {
  const icon = suggestion.skillIcon || getDefaultSkillIcon(suggestion.skillId);

  return html`
    <div class="skill-suggestion" data-skill-id="${suggestion.skillId}">
      <div class="skill-suggestion__icon">${icon}</div>
      <div class="skill-suggestion__content">
        <div class="skill-suggestion__header">
          <span class="skill-suggestion__name">${suggestion.skillName}</span>
        </div>
        <div class="skill-suggestion__reason">${suggestion.reason}</div>
      </div>
      <button
        class="skill-suggestion__connect-btn"
        type="button"
        @click=${() => onConnect?.(suggestion)}
      >
        Connect ${suggestion.skillName}
      </button>
    </div>
  `;
}

/**
 * Renders multiple skill suggestions.
 */
export function renderSkillSuggestions(
  suggestions: SkillSuggestion[],
  onConnect?: SkillSuggestionConnectHandler,
) {
  if (!suggestions || suggestions.length === 0) {
    return nothing;
  }

  return html`
    <div class="skill-suggestions">
      ${suggestions.map((s) => renderSkillSuggestion(s, onConnect))}
    </div>
  `;
}

/**
 * Returns default icon for known skill IDs.
 */
function getDefaultSkillIcon(skillId: string): string {
  const normalized = skillId.toLowerCase();

  if (normalized.includes("gmail") || normalized.includes("email")) {
    return "\u2709\ufe0f"; // envelope
  }
  if (normalized.includes("calendar")) {
    return "\ud83d\udcc5"; // calendar
  }
  if (normalized.includes("slack")) {
    return "\ud83d\udcac"; // speech bubble
  }
  if (normalized.includes("notion")) {
    return "\ud83d\udcdd"; // memo
  }
  if (normalized.includes("github")) {
    return "\ud83d\udc19"; // octopus
  }
  if (normalized.includes("browser") || normalized.includes("web")) {
    return "\ud83c\udf10"; // globe
  }

  return "\u26a1"; // lightning bolt - default
}

/**
 * CSS styles for skill suggestion cards.
 * These should be added to the main chat styles.
 */
export const skillSuggestionStyles = `
  .skill-suggestions {
    display: flex;
    flex-direction: column;
    gap: var(--space-2, 8px);
    margin-top: var(--space-3, 12px);
  }

  .skill-suggestion {
    display: flex;
    align-items: center;
    gap: var(--space-3, 12px);
    padding: var(--space-3, 12px) var(--space-4, 16px);
    background: var(--bg-elevated, #1a1a1f);
    border: 1px solid var(--border-color, #333);
    border-radius: var(--radius-lg, 12px);
    transition: border-color 0.15s ease;
  }

  .skill-suggestion:hover {
    border-color: var(--rax-yellow, #ffd000);
  }

  .skill-suggestion__icon {
    font-size: 24px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 208, 0, 0.1);
    border-radius: var(--radius-md, 8px);
    flex-shrink: 0;
  }

  .skill-suggestion__content {
    flex: 1;
    min-width: 0;
  }

  .skill-suggestion__header {
    display: flex;
    align-items: center;
    gap: var(--space-2, 8px);
  }

  .skill-suggestion__name {
    font-weight: 600;
    color: var(--text-primary, #f5f5f5);
    font-size: var(--font-sm, 14px);
  }

  .skill-suggestion__reason {
    font-size: var(--font-xs, 12px);
    color: var(--text-secondary, #999);
    margin-top: var(--space-1, 4px);
    line-height: 1.4;
  }

  .skill-suggestion__connect-btn {
    flex-shrink: 0;
    padding: var(--space-2, 8px) var(--space-4, 16px);
    background: var(--rax-yellow, #ffd000);
    color: var(--rax-black, #0b0b0f);
    border: none;
    border-radius: var(--radius-md, 8px);
    font-size: var(--font-sm, 14px);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
  }

  .skill-suggestion__connect-btn:hover {
    background: var(--rax-gold, #e6bc00);
    box-shadow: 0 0 12px rgba(255, 208, 0, 0.3);
  }

  .skill-suggestion__connect-btn:active {
    transform: scale(0.98);
  }

  @media (max-width: 640px) {
    .skill-suggestion {
      flex-wrap: wrap;
    }

    .skill-suggestion__connect-btn {
      width: 100%;
      margin-top: var(--space-2, 8px);
    }
  }
`;

/**
 * Detects skill suggestions from message text patterns.
 * This parses agent responses to find skill connection prompts.
 */
export function extractSkillSuggestionsFromText(text: string): SkillSuggestion[] {
  const suggestions: SkillSuggestion[] = [];

  // Pattern: [Connect Gmail] or [Connect Google Calendar] style buttons
  const buttonPattern = /\[Connect\s+([^\]]+)\]/gi;
  let match: RegExpExecArray | null;

  while ((match = buttonPattern.exec(text)) !== null) {
    const skillName = match[1].trim();
    const suggestion = inferSkillFromName(skillName);
    if (suggestion) {
      suggestions.push(suggestion);
    }
  }

  // Pattern: detect skill mentions with "not connected" or "don't have access"
  const accessPatterns = [
    /I (?:don't|do not) have access to (?:your )?(\w+(?:\s+\w+)?)/gi,
    /(\w+(?:\s+\w+)?) (?:is not|isn't) connected/gi,
    /need to connect (?:your )?(\w+(?:\s+\w+)?)/gi,
    /connect (?:your )?(\w+(?:\s+\w+)?) (?:first|to continue)/gi,
  ];

  for (const pattern of accessPatterns) {
    while ((match = pattern.exec(text)) !== null) {
      const skillName = match[1].trim();
      const suggestion = inferSkillFromName(skillName);
      if (suggestion && !suggestions.some((s) => s.skillId === suggestion.skillId)) {
        suggestions.push(suggestion);
      }
    }
  }

  return suggestions;
}

/**
 * Infers skill details from a skill name mention.
 */
function inferSkillFromName(name: string): SkillSuggestion | null {
  const normalized = name.toLowerCase();

  if (normalized.includes("gmail") || normalized === "email") {
    return {
      skillId: "gmail",
      skillName: "Gmail",
      integration: "gmail",
      reason: "Connect Gmail to read and send emails",
    };
  }

  if (normalized.includes("calendar") || normalized.includes("google calendar")) {
    return {
      skillId: "calendar",
      skillName: "Google Calendar",
      integration: "calendar",
      reason: "Connect Google Calendar to manage events",
    };
  }

  if (normalized.includes("slack")) {
    return {
      skillId: "slack",
      skillName: "Slack",
      authProvider: "slack",
      reason: "Connect Slack to send and read messages",
    };
  }

  if (normalized.includes("notion")) {
    return {
      skillId: "notion",
      skillName: "Notion",
      authProvider: "notion",
      reason: "Connect Notion to access your workspace",
    };
  }

  if (normalized.includes("github")) {
    return {
      skillId: "github",
      skillName: "GitHub",
      authProvider: "github",
      reason: "Connect GitHub to manage repositories",
    };
  }

  return null;
}
